import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PTO_TYPE_LABELS, STATUS_STYLES, formatDateShort } from './hrUtils';

export const PTOAdminTab = () => {
  const [requests, setRequests] = useState(null);
  const [balances, setBalances] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    Promise.all([hrAPI.listPtoRequests(), hrAPI.allPtoBalances()])
      .then(([r, b]) => { setRequests(r.data); setBalances(b.data); })
      .catch(() => toast.error('Failed to load PTO data'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (id, status) => {
    try {
      await hrAPI.reviewPtoRequest(id, { status });
      toast.success(`Request ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review');
    }
  };

  const saveBalance = async (empId) => {
    const e = edits[empId];
    if (!e) return;
    try {
      await hrAPI.setPtoBalance(empId, {
        vacation: parseFloat(e.vacation) || 0,
        sick: parseFloat(e.sick) || 0,
        personal: parseFloat(e.personal) || 0,
      });
      toast.success('Balance updated');
      setEdits((prev) => { const n = { ...prev }; delete n[empId]; return n; });
      load();
    } catch (err) {
      toast.error('Failed to update balance');
    }
  };

  if (!requests || !balances) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  const pending = requests.filter((r) => r.status === 'pending');
  const reviewed = requests.filter((r) => r.status !== 'pending');

  return (
    <div className="space-y-6" data-testid="hr-admin-pto-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-800 text-lg">Pending Approvals <Badge className="bg-amber-100 text-amber-700 ml-2">{pending.length}</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="hr-admin-pending-pto">
          {pending.length === 0 && <p className="text-sm text-slate-400 py-2">No pending time off requests.</p>}
          {pending.map((r) => (
            <div key={r.id} className="p-3 rounded-xl border border-amber-200 bg-amber-50/40 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.employee_name} — {PTO_TYPE_LABELS[r.type] || r.type}</p>
                  <p className="text-xs text-slate-500">{formatDateShort(r.start_date)} → {formatDateShort(r.end_date)} · {r.days} business day{r.days === 1 ? '' : 's'}{r.reason ? ` · ${r.reason}` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 rounded-full" onClick={() => review(r.id, 'approved')} data-testid={`hr-admin-approve-${r.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50 rounded-full" onClick={() => review(r.id, 'denied')} data-testid={`hr-admin-deny-${r.id}`}>
                    <X className="w-4 h-4 mr-1" /> Deny
                  </Button>
                </div>
              </div>
              {(r.conflicts || []).length > 0 && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded-lg px-3 py-2" data-testid={`hr-admin-conflict-${r.id}`}>
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <div>
                    <p className="font-semibold">Coverage warning — overlaps other leave:</p>
                    {r.conflicts.map((c) => (
                      <p key={c.id}>{c.employee_name} · {PTO_TYPE_LABELS[c.type] || c.type} · {formatDateShort(c.start_date)} → {formatDateShort(c.end_date)} ({c.status})</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">Employee PTO Balances</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hr-admin-balances-table">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-4">Employee</th>
                  <th className="py-2 pr-4">Vacation</th>
                  <th className="py-2 pr-4">Sick</th>
                  <th className="py-2 pr-4">Personal</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => {
                  const e = edits[b.employee_id];
                  return (
                    <tr key={b.employee_id} className="border-b border-slate-100">
                      <td className="py-2 pr-4">
                        <p className="font-medium text-slate-800">{b.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{b.role}</p>
                      </td>
                      {['vacation', 'sick', 'personal'].map((k) => (
                        <td key={k} className="py-2 pr-4">
                          {e ? (
                            <Input type="number" step="0.5" value={e[k]} className="w-20 h-8 bg-white border-slate-200"
                              onChange={(ev) => setEdits({ ...edits, [b.employee_id]: { ...e, [k]: ev.target.value } })}
                              data-testid={`hr-admin-balance-${k}-${b.employee_id}`} />
                          ) : (
                            <span className="text-slate-700">{b[k]}</span>
                          )}
                        </td>
                      ))}
                      <td className="py-2 text-right">
                        {e ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 rounded-full h-8" onClick={() => saveBalance(b.employee_id)} data-testid={`hr-admin-balance-save-${b.employee_id}`}>Save</Button>
                            <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => setEdits((prev) => { const n = { ...prev }; delete n[b.employee_id]; return n; })}>Cancel</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="rounded-full h-8"
                            onClick={() => setEdits({ ...edits, [b.employee_id]: { vacation: b.vacation, sick: b.sick, personal: b.personal } })}
                            data-testid={`hr-admin-balance-edit-${b.employee_id}`}>Edit</Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {reviewed.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-slate-800 text-lg">Request History</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {reviewed.slice(0, 20).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                <div>
                  <p className="text-sm font-medium text-slate-800">{r.employee_name} — {PTO_TYPE_LABELS[r.type] || r.type}</p>
                  <p className="text-xs text-slate-500">{formatDateShort(r.start_date)} → {formatDateShort(r.end_date)} · {r.days} day{r.days === 1 ? '' : 's'} · reviewed by {r.reviewed_by}</p>
                </div>
                <Badge className={STATUS_STYLES[r.status] || ''}>{r.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
